import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import {
  fieldsApi,
  type FieldDetail,
  type FieldSummary,
  type FieldTypeInfo,
  type FieldVisibilityOption,
} from '@/api/fields';
import { FieldCoordinatePicker } from '@/components/Fields/FieldCoordinatePicker';
import {
  DATA_OPERATOR_ORG_ID,
  FIELD_VISIBILITY_LABELS,
} from '@/constants/fieldVisibility';
import { useAuth } from '@/hooks/useAuth';
import {
  buildPointCoordinate,
  parsePointCoordinate,
  type LatLng,
} from '@/utils/fieldCoordinate';

export interface FieldFormInitialValues {
  id: string;
  name: string;
  type_id: string;
  visibility_status_id: string | null;
  coordinate: string | null;
  description: string | null;
}

interface FieldFormModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialValues?: FieldFormInitialValues;
  onSaved?: (field: FieldSummary) => void;
}

export const FieldFormModal: React.FC<FieldFormModalProps> = ({
  open,
  onClose,
  mode,
  initialValues,
  onSaved,
}) => {
  const { user } = useAuth();
  const isDataOperator = user?.organization_id === DATA_OPERATOR_ORG_ID;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [typeId, setTypeId] = useState('');
  const [visibilityStatusId, setVisibilityStatusId] = useState('');
  const [position, setPosition] = useState<LatLng | null>(null);
  const [fieldTypes, setFieldTypes] = useState<FieldTypeInfo[]>([]);
  const [visibilityOptions, setVisibilityOptions] = useState<FieldVisibilityOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const loadOptions = async () => {
      setOptionsLoading(true);
      try {
        const [typesResponse, visibilityResponse] = await Promise.all([
          fieldsApi.getFieldTypes(),
          fieldsApi.getVisibilityOptions(),
        ]);
        setFieldTypes(typesResponse.types);
        setVisibilityOptions(visibilityResponse.options);
      } catch (err) {
        console.error('Failed to load field form options:', err);
        setError('Failed to load form options');
      } finally {
        setOptionsLoading(false);
      }
    };

    void loadOptions();
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === 'edit' && initialValues) {
      setName(initialValues.name);
      setDescription(initialValues.description ?? '');
      setTypeId(initialValues.type_id);
      setVisibilityStatusId(initialValues.visibility_status_id ?? '');
      setPosition(parsePointCoordinate(initialValues.coordinate));
    } else {
      setName('');
      setDescription('');
      setTypeId('');
      setVisibilityStatusId('');
      setPosition(null);
    }
    setError(null);
  }, [open, mode, initialValues]);

  useEffect(() => {
    if (!open || mode !== 'create' || typeId || fieldTypes.length === 0) {
      return;
    }
    setTypeId(fieldTypes[0].id);
  }, [open, mode, typeId, fieldTypes]);

  useEffect(() => {
    if (!open || !isDataOperator || visibilityStatusId || visibilityOptions.length === 0) {
      return;
    }
    const companyPublic = visibilityOptions.find((o) => o.name === 'company_public');
    setVisibilityStatusId(companyPublic?.id ?? visibilityOptions[0].id);
  }, [open, isDataOperator, visibilityStatusId, visibilityOptions]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Field name is required');
      return;
    }
    if (!typeId) {
      setError('Field type is required');
      return;
    }

    setLoading(true);
    setError(null);

    const coordinate = buildPointCoordinate(position?.lat ?? null, position?.lng ?? null);
    const normalizedDescription = description.trim() || null;
    const payload = {
      name: trimmedName,
      type_id: typeId,
      coordinate,
      description: normalizedDescription,
      ...(isDataOperator && visibilityStatusId
        ? { visibility_status_id: visibilityStatusId }
        : {}),
    };

    try {
      let saved: FieldSummary;
      if (mode === 'edit' && initialValues) {
        saved = await fieldsApi.updateField(initialValues.id, payload);
      } else {
        saved = await fieldsApi.createField(payload);
      }
      onSaved?.(saved);
      handleClose();
    } catch (err) {
      console.error('Failed to save field:', err);
      setError(mode === 'edit' ? 'Failed to update field' : 'Failed to create field');
    } finally {
      setLoading(false);
    }
  };

  const title = mode === 'edit' ? 'Edit Field' : 'Add Field';
  const submitLabel = mode === 'edit' ? 'Save' : 'Create';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle>
        <Typography variant="h6">{title}</Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {optionsLoading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : fieldTypes.length === 0 ? (
          <Alert severity="warning">
            Field types could not be loaded. Check that the backend is running and up to date.
          </Alert>
        ) : (
          <>
            <TextField
              fullWidth
              label="Field name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              sx={{ mb: 2, mt: 1 }}
            />

            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={2}
              maxRows={4}
              placeholder="Optional field description or data source"
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Field type</InputLabel>
              <Select
                value={typeId}
                label="Field type"
                onChange={(e) => setTypeId(e.target.value)}
              >
                {fieldTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isDataOperator && visibilityOptions.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={visibilityStatusId}
                  label="Visibility"
                  onChange={(e) => setVisibilityStatusId(e.target.value)}
                >
                  {visibilityOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {FIELD_VISIBILITY_LABELS[option.name] ?? option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FieldCoordinatePicker
              enabled={open}
              position={position}
              onChange={setPosition}
              markerKind="field"
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || optionsLoading || !name.trim() || !typeId}
        >
          {loading ? <CircularProgress size={20} /> : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Build edit form values from field detail + resolved type id. */
export function fieldDetailToFormValues(
  detail: FieldDetail,
  typeId: string,
): FieldFormInitialValues {
  return {
    id: detail.id,
    name: detail.name,
    type_id: typeId,
    visibility_status_id: detail.visibility_status_id,
    coordinate: detail.coordinate,
    description: detail.description,
  };
}
